import React, { useState, useEffect } from 'react';
import { MessageCircle, PhoneCall, ArrowUp, ArrowDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const FloatingButtons = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(false);

    useEffect(() => {
        const toggleVisibility = () => {
            // Show top button after 300px
            if (window.scrollY > 300) {
                setIsVisible(true);
            } else {
                setIsVisible(false);
            }

            // Hide bottom button if near the end
            const totalHeight = document.documentElement.scrollHeight;
            const scrollPos = window.scrollY + window.innerHeight;
            if (scrollPos > totalHeight - 100) {
                setIsAtBottom(true);
            } else {
                setIsAtBottom(false);
            }
        };

        window.addEventListener('scroll', toggleVisibility);
        return () => window.removeEventListener('scroll', toggleVisibility);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const scrollToBottom = () => {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
    };

    return (
        <>
            <div className="flex flex-col fixed bottom-4 left-4 md:bottom-6 md:left-6 gap-2 md:gap-4 z-[1000]">
                {/* Left Side: Main Contact Buttons */}
                <motion.a
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                    href="https://wa.me/918708604830?text=i%20have%20query%20about%20my%20project"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 md:w-14 md:h-14 bg-green-500 text-white rounded-full flex items-center justify-center shadow-lg shadow-green-500/20 hover:bg-green-600 transition-all relative group"
                >
                    <MessageCircle size={20} className="md:w-7 md:h-7" />
                    <span className="absolute top-0 right-0 flex h-2.5 w-2.5 md:h-4 md:w-4">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 md:h-4 md:h-4 bg-white border-2 md:border-4 border-green-500"></span>
                    </span>
                </motion.a>

                <motion.a
                    whileHover={{ scale: 1.1, rotate: -5 }}
                    whileTap={{ scale: 0.9 }}
                    href="tel:+918708604830"
                    className="w-10 h-10 md:w-14 md:h-14 bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg shadow-red-600/20 hover:bg-red-700 transition-all"
                >
                    <PhoneCall size={18} className="md:w-6 md:h-6" />
                </motion.a>
            </div>

            {/* Right Side: Small Navigation Buttons (Moved to Bottom Right) */}
            <div className="flex flex-col fixed bottom-4 right-4 md:bottom-6 md:right-6 gap-1.5 md:gap-2 z-[1000]">
                <AnimatePresence>
                    {isVisible && (
                        <motion.button
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={scrollToTop}
                            className="w-9 h-9 md:w-11 md:h-11 bg-white text-primary rounded-xl flex items-center justify-center shadow-xl border border-slate-100 hover:bg-primary hover:text-white transition-all"
                            title="Scroll to Top"
                        >
                            <ArrowUp size={18} className="md:size-5" />
                        </motion.button>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {!isAtBottom && (
                        <motion.button
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={scrollToBottom}
                            className="w-9 h-9 md:w-11 md:h-11 bg-white text-primary rounded-xl flex items-center justify-center shadow-xl border border-slate-100 hover:bg-primary hover:text-white transition-all"
                            title="Scroll to Bottom"
                        >
                            <ArrowDown size={18} className="md:size-5" />
                        </motion.button>
                    )}
                </AnimatePresence>
            </div>
        </>
    );
};

export default FloatingButtons;
